import React, { useState, useEffect, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import Context from "@components/useContext/context";
import { gtm } from "@analytics/bike/commonActions";
import CustomRouter from "@components/common/router";
import { discoverIplCampaign } from "@utils/landing-page-config";
import {
  bikeIdentifedSuccessScreenLoads,
  bikeMMVScreenLoaded,
  bikeRegNoEditClicked,
  bikeRegYearEnteredClicked,
  bikePreviousPolicyStatusClicked,
  bikeAgeSelected,
  bikePreviousPolicyExpirySelected,
  notYourBikeClicked,
  viewPricesCTAClicked,
  popularBikeSelected,
  makeModelSelected
} from "@analytics/bike/actions/mmv";
import { BasicLoader } from "@acko-tech/building-blocks.ui.loaders";

import { CreateProposalData } from "@store/bikeProposalData/types.d";
import { whatsappOptinPrelogin } from "@store/indexModule/apis";

import {
  getProposalData,
  updateProposalData
} from "@store/moData/proposalData/actions";

import { resetProposalData } from "@store/moData/proposalData/actions";

import { FormData, FormMeta, QuestionOption } from "./types";

import {
  REGISTRATION_YEARS,
  POLICY_EXPIRY_OPTIONS
} from "@utils/bike/bikeConstants";

import { getMMVDataRequest } from "@store/mmvData/actions";
import { Variant } from "@store/mmvData/types";
import { BikeOdRange } from "@utils/bike/bikeOdRange";

import validate from "@utils/validate";
import { useRunOnceHook } from "@components/common/custom-hooks";
import {
  onMMVScreenWithAutofillLoaded,
  onPopularBikeSelected,
  onTypedBikeSelected,
  tapBtnRegYear,
  viewPlansClicked
} from "segments/eventActions/bike/prequoteModule";
import { journeyEnum } from "@utils/journeyEnum";
import {
  getFromSessionStorage,
  setInSessionStorage
} from "@utils/handleSessionStorage";

const MMVMobile = dynamic(() => import("./mmvD2c"));

const initialFormData: FormData = {
  variant: null,
  registrationYear: null,
  policyExpiry: null,
  phone: "",
  ncb: ""
};

const initialFormMeta: FormMeta = {
  valid: true,
  variant: false,
  registrationYear: false,
  policyExpiry: false
  // phone: false
};

const currentYear = new Date().getFullYear().toString();

function isMetaValid(formMeta: FormMeta): boolean {
  return (
    !formMeta.variant && !formMeta.registrationYear && !formMeta.policyExpiry
    // && !formMeta.phone
  );
}

function clearMetaError(formMeta: FormMeta, key: keyof FormMeta): FormMeta {
  const draftFormMeta: FormMeta = { ...formMeta };

  switch (key) {
    case "policyExpiry":
    case "registrationYear":
    case "variant":
      // case "phone":
      draftFormMeta[key] = false;
      break;

    default:
      break;
  }

  draftFormMeta.valid = isMetaValid(draftFormMeta);
  return draftFormMeta;
}

function validateData(formData: FormData /*, setMobileErrors*/): FormMeta {
  const formMeta: FormMeta = { ...initialFormMeta };

  // const mobileNumberError = validate.phoneNumber({
  //   phoneNumber: formData.phone
  // });
  // if (mobileNumberError.phoneNumber) {
  //   setMobileErrors(mobileNumberError.phoneNumber);
  // }

  if (!formData.variant?.id) {
    formMeta.valid = false;
    formMeta.variant = true;
  }

  if (!formData.registrationYear?.value) {
    formMeta.valid = false;
    formMeta.registrationYear = true;
  }

  if (
    formData.registrationYear?.value !== currentYear &&
    !formData.policyExpiry?.value
  ) {
    formMeta.valid = false;
    formMeta.policyExpiry = true;
  }

  return formMeta;
}

interface State {
  fetchingMMV?: boolean;
  fetchingProposal?: boolean;
  fetchProposalSuccess?: boolean;
  registrationYear: string;
  policyExpired?: boolean;
  variant: Variant | null;
  expiryStatus?: string;
  variant_ekey?: string;
  vehicles: any;
  vehicleDetails: any;
  registrationNumber: string | undefined;
  proposalPhone: string;
  registeredPhone: string;
  ncb: string;
}

function getMMV(ekey: string | undefined, vehicles: any): Variant | null {
  let variant: any = null;
  if (vehicles?.length && ekey) {
    vehicles.find(vehicle => {
      const tmp_var = vehicle.variants.find(variant => variant.id === ekey);

      if (tmp_var) {
        const vehicleMake = `${vehicle.name} ${
          tmp_var.fuelType === "electric"
            ? `${(tmp_var.cc / 1000).toFixed(1)} kW`
            : `${tmp_var.cc}CC`
        }`;
        tmp_var.verbose = vehicleMake;
        variant = {
          ...tmp_var,
          image: vehicle?.image
        };
      }
      return Boolean(variant);
    });
  }
  return variant;
}

const Landing: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formMeta, setFormMeta] = useState<FormMeta>(initialFormMeta);
  const [showMMVPicker, setShowMMVPicker] = useState<boolean>(false);
  const [showRegYearPicker, setShowRegYearPicker] = useState<boolean>(false);
  const [showExpiryPicker, setShowExpiryPicker] = useState<boolean>(false);
  const [disableExpiryPicker, setDisableExpiryPicker] = useState<boolean>(
    false
  );
  const [isNewBike, setIsNewBike] = useState(false);
  const [mmvInfo, setMMVInfo] = useState(
    "Please provide more details to view plans"
  );
  const router = useRouter();
  const [title, setTitle] = useState("Enter your bike details");

  const { proposal_id: proposalId, flow, src, lsi_portal_flow } = router.query;
  const isLsiPortalFlow = lsi_portal_flow === "true";

  const {
    fetchingMMV,
    fetchingProposal,
    fetchProposalSuccess,
    registrationYear,
    policyExpired,
    expiryStatus,
    variant_ekey,
    variant,
    vehicleDetails,
    vehicles,
    registrationNumber,
    proposalPhone,
    registeredPhone,
    ncb
  }: State = useSelector(
    ({ mmvDataReducer, proposalDataReducer, indexReducer }: any) => {
      console.log({ mmvDataReducer, proposalDataReducer, indexReducer });

      return {
        fetchingMMV: mmvDataReducer?.isFetching,
        fetchingProposal: proposalDataReducer?.isFetching,
        fetchProposalSuccess: proposalDataReducer?.isSuccess,
        variant_id: proposalDataReducer?.data?.variantId,
        variant_ekey: proposalDataReducer?.data?.variantId,
        variant: getMMV(
          proposalDataReducer?.data?.variantId,
          mmvDataReducer?.vehicles
        ),
        vehicleDetails: proposalDataReducer?.data,
        registrationNumber: proposalDataReducer?.data?.carNumber,
        registrationYear:
          proposalDataReducer?.data?.registrationYear?.toString() ?? "",
        policyExpired: proposalDataReducer?.data?.expiry,
        expiryStatus: proposalDataReducer?.data?.policyStatus,
        vehicles: mmvDataReducer?.vehicles,
        proposalPhone: proposalDataReducer?.data?.mobileNumber,
        registeredPhone: indexReducer?.mobileNumber,
        ncb: proposalDataReducer?.data?.ncb
      };
    }
  );

  const { basePath, product } = useContext(Context);
  const [mobileErrors, setMobileErrors] = useState("");
  // const [whatsAppOpt, setWhatsAppOpt] = useState(true);
  const ref = useRef(false);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getMMVDataRequest());
  }, []);

  useEffect(() => {
    if (!vehicles) dispatch(getMMVDataRequest());
    if (proposalId) {
      dispatch(
        getProposalData({
          proposalId: proposalId,
          skipNavigation: true,
          callback: proposal => {
            const mmv_version = proposal?.data?.mmv_version;
            setInSessionStorage("mmv_version", mmv_version);
          }
        })
      );
    }
  }, [proposalId]);

  useEffect(() => {
    if (!formData.variant?.verbose) {
      if (!variant?.verbose) {
        setFormData({
          ...formData,
          variant: getMMV(variant_ekey, vehicles)
        });
      } else {
        setFormData({
          ...formData,
          variant
        });
      }
    }
  }, [variant, vehicles, variant_ekey]);

  useEffect(() => {
    if (
      variant?.verbose &&
      registrationYear &&
      registrationYear !== currentYear &&
      (!expiryStatus ||
        typeof policyExpired === "undefined" ||
        policyExpired === null)
    ) {
      setMMVInfo(
        "Please verify your details and tell us your current policy expiry status"
      );
    }
    if (
      flow === "new" ||
      registrationYear === currentYear ||
      (variant?.verbose &&
        registrationYear &&
        (expiryStatus ||
          (typeof policyExpired !== "undefined" && policyExpired !== null)))
    ) {
      setMMVInfo("Please verify your details and proceed to view plans");
    }
    if (
      !variant?.verbose &&
      registrationYear &&
      (expiryStatus ||
        (typeof policyExpired !== "undefined" && policyExpired !== null))
    ) {
      setMMVInfo("Please verify your details and proceed to view plans");
    }
    // console.log([variant, registrationYear, expiryStatus]);
  }, [variant, registrationYear, expiryStatus]);

  // R2D2 Events
  function getVehicleEdata(vehicleData: any) {
    if (vehicleData?.variantId || formData?.variant?.id) {
      return {
        reg_no: vehicleData?.carNumber,
        cc: vehicleData?.cc,
        fuel_type: vehicleData?.fuelType,
        previous_policy_expired: vehicleData?.expiry,
        previous_policy_expired_status:
          vehicleData?.expiry === false ? "0" : vehicleData?.policyStatus,
        registration_year: vehicleData?.registrationYear,
        vehicle_make_model: vehicleData?.model,
        vehicle_variant: formData?.variant
      };
    }
    return null;
  }

  function getScreenData(vehicleData, flow) {
    let screen = "bike_aadrilla_failure_screen";
    if (vehicleData?.variant_id) {
      screen = "bike_aadrilla_success_screen";
    }
    if (flow === "dkbn") {
      screen = "dont_know_bike_number_screen";
    }
    if (flow === "new") {
      screen = "new_bike_screen";
    }
    return screen;
  }

  function clearFormData(type?: string) {
    // if (vehicleDetails?.variant_id) {
    const eData = getVehicleEdata(vehicleDetails);
    if (type && type === "regNo") {
      dispatch(bikeRegNoEditClicked(eData, Boolean(vehicleDetails?.variantId)));
      setFormData({ ...formData, variant: null });
    } else {
      dispatch(notYourBikeClicked(eData, Boolean(vehicleDetails?.variantId)));
    }
    // }
    // setFormData({ ...formData, variant: null });
  }

  useEffect(() => {
    if (registrationYear) {
      const yearOption =
        REGISTRATION_YEARS?.find(option => option.value === registrationYear) ??
        null;

      setFormData(data => ({
        ...data,
        registrationYear: yearOption
      }));
    } else {
      setFormData(data => ({
        ...data,
        registrationYear: null
      }));
    }

    if (registrationYear === currentYear && flow === "new") {
      setDisableExpiryPicker(true);
    }
  }, [registrationYear]);

  useEffect(() => {
    if (typeof policyExpired === "boolean") {
      const selectedOption = policyExpired ? expiryStatus : "0";
      const policyExpiry =
        POLICY_EXPIRY_OPTIONS.find(option => option.value === selectedOption) ??
        null;

      setFormData(data => ({
        ...data,
        policyExpiry
      }));
    } else {
      setFormData(data => ({
        ...data,
        policyExpiry: null
      }));
    }
    // this check is added to prevent autofilling of prev policy
    // in Bike OD cases
    if (
      BikeOdRange(registrationYear) &&
      !getFromSessionStorage("prevPolicyExpiryRangeID")
    ) {
      setFormData(data => ({
        ...data,
        policyExpiry: null
      }));
    }
  }, [policyExpired, expiryStatus, registrationYear]);

  useEffect(() => {
    // This ref is used to run this r2d2 event only once,
    // after firing this r2d2 event its set to true
    if (fetchProposalSuccess && ref.current === false) {
      if (vehicleDetails?.variantId) {
        dispatch(
          bikeIdentifedSuccessScreenLoads(getVehicleEdata(vehicleDetails))
        );
        dispatch(
          onMMVScreenWithAutofillLoaded({
            ...getVehicleEdata(vehicleDetails),
            cc: vehicleDetails?.cc && vehicleDetails?.cc.toString(),
            vehicle_variant:
              vehicleDetails?.variant && vehicleDetails?.variant.toString()
          })
        );
        setTitle("Verify your bike details");
      } else if (vehicleDetails?.isNew !== null || flow === "new") {
        const screen = getScreenData(vehicleDetails, flow);
        dispatch(bikeMMVScreenLoaded(registrationNumber, screen));
      }
      ref.current = true;
    }
  }, [vehicleDetails, fetchProposalSuccess]);

  useRunOnceHook({
    hookCondition: fetchProposalSuccess || isNewBike,
    callback: () => {
      dispatch(
        onMMVScreenWithAutofillLoaded({
          journey: journeyEnum[product]
        })
      );
    }
  });

  useEffect(() => {
    if (showRegYearPicker) {
      dispatch(
        bikeRegYearEnteredClicked(
          getVehicleEdata(vehicleDetails),
          getScreenData(vehicleDetails, flow)
        )
      );
      dispatch(tapBtnRegYear());
    }
  }, [showRegYearPicker]);

  useEffect(() => {
    if (showExpiryPicker) {
      dispatch(
        bikePreviousPolicyStatusClicked(
          getVehicleEdata(vehicleDetails),
          getScreenData(vehicleDetails, flow)
        )
      );
    }
  }, [showExpiryPicker]);

  function handleVariantChange(variant: Variant | null, popular?: boolean) {
    const draftFormData: FormData = { ...formData, variant };
    if (variant) {
      const eData = {
        reg_no: registrationNumber,
        vehicle_make_model: variant?.verbose
      };
      if (popular) {
        dispatch(
          popularBikeSelected(eData, getScreenData(vehicleDetails, flow))
        );
        dispatch(onPopularBikeSelected());
      } else {
        dispatch(makeModelSelected(eData, getScreenData(vehicleDetails, flow)));
        dispatch(onTypedBikeSelected());
      }
    }
    if (!formMeta.valid && formMeta.variant) {
      const draftFormMeta = clearMetaError(formMeta, "variant");
      setFormMeta(draftFormMeta);
    }

    setShowMMVPicker(false);
    setFormData(draftFormData);
  }

  function onMobileNumberChange(phoneNumber) {
    // let draftFormMeta = { ...formMeta };
    let draftFormData = { ...formData };
    draftFormData = { ...draftFormData, phone: phoneNumber };
    setFormData(draftFormData);
    draftFormData = { ...draftFormData, phone: phoneNumber };
    setFormData(draftFormData);
    const mobileNumberError = validate.phoneNumber({ phoneNumber });
    if (mobileNumberError.phoneNumber) {
      setMobileErrors(mobileNumberError.phoneNumber);
    } else {
      setMobileErrors("");
    }
  }

  function lpRedirect() {
    dispatch(resetProposalData());
    clearFormData("regNo");
    if (src === "hp") {
      window.location.href = `${process.env.HOST_URL}/bike/?reg=${registrationNumber}`;
    } else if (basePath === discoverIplCampaign) {
      window.location.href = `${process.env.HOST_URL}${basePath}?reg=${registrationNumber}`;
    } else {
      CustomRouter.push(
        {
          pathname: "/lp/new-bike",
          query: {
            migration: "bike",
            reg: registrationNumber
          }
        },
        `/lp/new-bike?reg=${registrationNumber}&migration=bike`
      );
    }
  }

  function handleRegYearChange(registrationYear: QuestionOption) {
    const draftFormData: FormData = { ...formData, registrationYear };
    let draftFormMeta = { ...formMeta };
    if (
      BikeOdRange(Number(registrationYear.value)) !==
      BikeOdRange(
        Number(
          formData.registrationYear?.value !== undefined
            ? formData.registrationYear?.value
            : null
        )
      )
    ) {
      // clearing policy expiry status when switching to or from OD
      draftFormData.policyExpiry = null;
    }

    if (registrationYear.value === currentYear && flow === "new") {
      draftFormData.policyExpiry = null;

      if (!formMeta.valid && formMeta.policyExpiry) {
        draftFormMeta = clearMetaError(draftFormMeta, "policyExpiry");
      }

      setDisableExpiryPicker(true);
    } else if (disableExpiryPicker) {
      setDisableExpiryPicker(false);
    }

    if (!formMeta.valid && formMeta.registrationYear) {
      draftFormMeta = clearMetaError(draftFormMeta, "registrationYear");
      setFormMeta(draftFormMeta);
    }
    const { value: registration_year } = registrationYear;
    const eData = { ...getVehicleEdata(vehicleDetails), registration_year };
    const screen = getScreenData(vehicleDetails, flow);
    dispatch(bikeAgeSelected(eData, screen));

    setShowRegYearPicker(false);
    setFormMeta(draftFormMeta);
    setFormData(draftFormData);
  }

  useEffect(() => {
    if (flow === "new") {
      const registrationYear = {
        name: currentYear,
        buttonId: currentYear,
        value: currentYear
      };
      setIsNewBike(true);
      handleRegYearChange(registrationYear);
    }
  }, [flow]);

  useEffect(() => {
    const phone = proposalPhone || registeredPhone;
    setFormData(data => ({
      ...data,
      phone
    }));
  }, [proposalPhone, registeredPhone]);

  useEffect(() => {
    if (ncb != null) {
      setFormData(data => ({
        ...data,
        ncb: ncb
      }));
    }
  }, [ncb]);

  function handleExpiryChange(policyExpiry: QuestionOption) {
    const draftFormData: FormData = { ...formData, policyExpiry };
    if (!formMeta.valid && formMeta.policyExpiry) {
      const draftFormMeta = clearMetaError(formMeta, "policyExpiry");
      setFormMeta(draftFormMeta);
    }
    const expired = policyExpiry!.value !== "0";
    const eData = {
      ...getVehicleEdata(vehicleDetails),
      previous_policy_expired_status: policyExpiry.value,
      previous_policy_expired: expired
    };
    dispatch(
      bikePreviousPolicyExpirySelected(
        eData,
        getScreenData(vehicleDetails, flow)
      )
    );

    setShowExpiryPicker(false);
    setFormData(draftFormData);
  }

  function handleFormSubmit() {
    let draftFormMeta = validateData(formData);

    const mobileNumberError = validate.phoneNumber({
      phoneNumber: formData.phone
    });
    if (isNewBike && !isLsiPortalFlow && mobileNumberError.phoneNumber) {
      setMobileErrors(mobileNumberError.phoneNumber);
    }

    const callback = vehicleData => {
      dispatch(
        viewPricesCTAClicked(
          getVehicleEdata(vehicleData),
          getScreenData(vehicleData, flow)
        )
      );
      if (window?.wisepops) {
        window?.wisepops("properties", {
          proposalId: vehicleData?.proposal_id
        });
      }
    };
    if (
      !draftFormMeta.valid ||
      (isNewBike && !isLsiPortalFlow && mobileNumberError.phoneNumber)
    ) {
      setFormMeta(draftFormMeta);
    } else {
      const data: CreateProposalData = {
        variant_ekey: formData.variant!.id,
        registration_year: formData.registrationYear!.value,
        phone: formData.phone,
        ncb: formData.ncb
      };

      let previousPolicyExpiryRange = "";

      if (formData.policyExpiry) {
        data.previous_policy_expired = formData.policyExpiry.value !== "0";
        previousPolicyExpiryRange = "false";
      }

      if (data.previous_policy_expired) {
        data.previous_policy_status = formData.policyExpiry!.value as string;
        previousPolicyExpiryRange = data.previous_policy_status;
      }

      whatsappOptinPrelogin({
        update_user_profile: true,
        phone_number: formData.phone,
        optin: true
      });

      dispatch(viewPlansClicked());
      // setting proposal ID to get a unique mapping with a proposal
      setInSessionStorage("prevPolicyExpiryRangeID", proposalId);
      dispatch(
        gtm({
          event: "ViewPricesClickedBike",
          data: {
            purchase_year: formData.registrationYear!.value,
            previous_policy_expiry_range: previousPolicyExpiryRange,
            make_model: formData.variant!.verbose,
            ncb: formData.ncb
          }
        })
      );
      dispatch(
        updateProposalData({
          data: {
            ...data
          },
          proposalId,
          sendRequest: true,
          state: "preQuote",
          is_new: isNewBike,
          origin: isNewBike ? "acko_new_bike" : "acko_bike",
          callback: res => {
            callback(res?.data);
          }
        })
      );
    }
  }

  return (
    <>
      {(fetchingMMV || fetchingProposal) && <BasicLoader />}
      <MMVMobile
        formData={formData}
        formMeta={formMeta}
        registrationNumber={registrationNumber}
        showMMVPicker={showMMVPicker}
        setShowMMVPicker={setShowMMVPicker}
        proposalId={proposalId}
        onVariantChange={handleVariantChange}
        showRegYearPicker={showRegYearPicker}
        setShowRegYearPicker={setShowRegYearPicker}
        onPolicyExpiryChange={handleExpiryChange}
        showExpiryPicker={showExpiryPicker}
        disableExpiryPicker={disableExpiryPicker}
        setShowExpiryPicker={setShowExpiryPicker}
        onRegistrationYearChange={handleRegYearChange}
        onSubmit={handleFormSubmit}
        clearFormData={clearFormData}
        isNewBike={isNewBike}
        vehicleDetails={vehicleDetails}
        flow={flow}
        lpRedirect={lpRedirect}
        mmvInfo={mmvInfo}
        mobileNumber={formData.phone}
        onMobileNumberChange={onMobileNumberChange}
        mobileErrors={mobileErrors}
        title={title}
        currentYear={currentYear}
        fetchProposalSuccess={fetchProposalSuccess}
        registrationYear={registrationYear}
        isLsiPortalFlow={isLsiPortalFlow}
        // whatsAppOpt={whatsAppOpt}
        // setWhatsAppOpt={setWhatsAppOpt}
      />
    </>
  );
};

export default Landing;
